import List from '@mui/material/List';

import { useTranslation } from 'react-i18next';
import { FilterSection } from 'common/components/Filters/FilterSection.tsx';
import { FiltersDialog } from 'common/components/Filters/FiltersDialog.tsx';
import { FiltersButton } from 'common/components/Filters/FiltersButton.tsx';

import { FilterListItem } from 'common/components/Filters/FilterListItem.tsx';
import { useModalFilters } from 'common/components/Filters/useModalFilters.ts';

import { useAtom, useSetAtom } from 'jotai';
import {
  PRODUCTS_MANAGEMENT_INITIAL_VALUES,
  ProductsManagementFilters,
  productsManagementFiltersAtom,
  productsManagementPageFilterAtom,
} from 'atoms/filters/products-management-filters.ts';
import { useDataRangeCopy } from 'common/components/Inputs/DateRangePickerMui/hooks/useDataRangeParse.ts';
import { parseDateRange } from 'common/components/Inputs/DateRangePickerMui/utils/parseDateRange.ts';
import { StatusSelect } from '@/activityLog/common/Input/StatusSelect.tsx';
import { DateRangePicker } from '@/activityLog/common/Input/DateRangePicker/DateRangePicker.tsx';
import { getValidDateRange } from '@/activityLog/common/Input/DateRangePicker/utils.ts';

export const ProductsManagementFiltersModal = () => {
  const [filters, setFilters] = useAtom(productsManagementFiltersAtom);
  const setPage = useSetAtom(productsManagementPageFilterAtom);
  const onFiltersChange = (filters: ProductsManagementFilters) => {
    const { from, to } = parseDateRange({ from: filters.uploadDateFrom, to: filters.uploadDateTo });
    setFilters({
      ...filters,
      uploadDateFrom: from,
      uploadDateTo: to,
    });

    setPage(0);
  };

  const {
    filtersActive,
    open,
    openModal,
    setOpen,
    apply,
    cancel,
    clear,
    modalFilters,
    setModalFilters,
  } = useModalFilters(PRODUCTS_MANAGEMENT_INITIAL_VALUES, filters, onFiltersChange);
  const { t } = useTranslation();

  const dateRangeValue = useDataRangeCopy({
    from: modalFilters.uploadDateFrom,
    to: modalFilters.uploadDateTo,
  });

  return (
    <>
      <FiltersButton onClick={openModal} active={filtersActive} />
      <FiltersDialog
        open={open}
        onApply={apply}
        onCancel={cancel}
        onClear={clear}
        onClose={() => {
          cancel();
          setOpen(false);
        }}
      >
        <FilterSection>
          <List>
            <FilterListItem label={t('common.status')} htmlFor="select status">
              <StatusSelect
                data-testid="productsManagementStatusSelect"
                value={modalFilters.status}
                onChange={(status) => {
                  setModalFilters((prevFilters) => ({
                    ...prevFilters,
                    status: status,
                  }));
                }}
              />
            </FilterListItem>
          </List>
        </FilterSection>
        <FilterSection>
          <List>
            <FilterListItem label={t('common.uploadDate')} htmlFor="source">
              <DateRangePicker
                onChange={(value) => {
                  setModalFilters((prevFilters) => ({
                    ...prevFilters,
                    uploadDateFrom: value[0],
                    uploadDateTo: value[1],
                  }));
                }}
                value={dateRangeValue}
                onError={(err) => {
                  setModalFilters((prevFilters) => {
                    return getValidDateRange(err, prevFilters);
                  });
                }}
              />
            </FilterListItem>
          </List>
        </FilterSection>
      </FiltersDialog>
    </>
  );
};
